/**
 * Display formatters.
 *
 * Money, status labels, flag CSS classes. All pure functions.
 */

import type { Currency, Money } from '../types/common';

const CURRENCY_FORMATTERS = new Map<string, Intl.NumberFormat>();

function fmt(currency: Currency): Intl.NumberFormat {
  let f = CURRENCY_FORMATTERS.get(currency);
  if (!f) {
    f = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    CURRENCY_FORMATTERS.set(currency, f);
  }
  return f;
}

/** Convert minor units (e.g. 12345 fils) → display string (e.g. "AED 123.45"). */
export function formatMoney(m: Money): string {
  const major = m.amount / 100;
  return fmt(m.currency).format(major);
}

/** Convert major units → minor units for storage. */
export function toMinorUnits(major: number): number {
  return Math.round(major * 100);
}

/** Format minutes as `2h 30m`. */
export function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m.toString().padStart(2, '0')}m`;
}

/** Truncate a string with ellipsis. */
export function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}